<!-- Breadcrumbs (auto-generated from URL) -->
<app-page-header />

<!-- Title bar with back arrow + title + status badge -->
@if (contribution) {
  <div class="mx-auto flex max-w-5xl items-center gap-4 px-4 pb-4">
    <p-button
      icon="pi pi-arrow-left"
      (onClick)="backToList()"
      [text]="true"
      [rounded]="true"
      [pTooltip]="'common.back' | transloco"
    />
    <div class="flex-1">
      <h1 class="font-cabin text-2xl font-semibold text-greyscale-900">
        {{ contribution.trip.title }}
      </h1>
      <p class="text-sm text-greyscale-600">
        {{ getProjectName() }} · {{ getTotalCO2() | number:'1.2-2' }} kg CO2
      </p>
    </div>
    <span
      class="rounded-full px-3 py-1 text-sm font-medium"
      [ngClass]="getStatusClasses(contribution.status)"
    >
      {{ "contribute.status." + contribution.status | transloco }}
    </span>
  </div>
} @else {
  <div class="mx-auto flex max-w-5xl items-center gap-4 px-4 pb-4">
    <p-button
      icon="pi pi-arrow-left"
      (onClick)="backToList()"
      [text]="true"
      [rounded]="true"
      [pTooltip]="'common.back' | transloco"
    />
    <h1 class="font-cabin text-2xl font-semibold text-greyscale-900">
      {{ 'navigation.contributions' | transloco }}
    </h1>
  </div>
}

<main class="mx-auto max-w-5xl p-4">
  @if (loading) {
    <div class="flex h-64 items-center justify-center">
      <i class="pi pi-spin pi-spinner text-4xl text-primary-500"></i>
    </div>
  } @else if (contribution) {

    <!-- Main 2-column layout -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-5">
      <!-- Left Column: Action Card (Payment or Certificate) -->
      <div class="lg:col-span-3">
        @if (isPaid()) {
          <!-- PAID STATE: Certificate & Payment Info -->
          <div class="overflow-hidden rounded-lg border border-greyscale-200 bg-white">
            <div class="border-b border-greyscale-200 bg-validation-50 p-4">
              <div class="flex items-center gap-2">
                <i class="pi pi-check-circle text-xl text-validation-600"></i>
                <h2 class="text-lg font-semibold text-validation-700">{{ 'contribute.success.title' | transloco }}</h2>
              </div>
            </div>
            <div class="space-y-4 p-4">
              <!-- Certificate recipient -->
              <div>
                <p class="text-sm text-gray-500">{{ 'contribute.certificateDetails.recipientName' | transloco }}</p>
                <p class="text-lg font-semibold text-gray-900">{{ contribution.certificateRecipientName }}</p>
              </div>

              <!-- Download buttons -->
              <div class="flex flex-wrap gap-3">
                <button
                  pButton
                  icon="pi pi-download"
                  (click)="downloadCertificate()"
                  [disabled]="!canDownload()"
                >
                  {{ 'contribute.success.downloadCertificate' | transloco }}
                </button>
                <button
                  pButton
                  icon="pi pi-file-pdf"
                  severity="secondary"
                  (click)="downloadInvoice()"
                  [disabled]="!hasInvoice()"
                  [pTooltip]="!hasInvoice() ? ('contribute.success.invoicePending' | transloco) : ''"
                >
                  {{ 'contribute.success.downloadInvoice' | transloco }}
                </button>
              </div>

              <!-- Payment info -->
              <div class="border-t border-greyscale-200 pt-4">
                <p class="text-sm text-gray-500">{{ 'contribute.payment.method' | transloco }}</p>
                <p class="font-medium text-gray-900">
                  @if (contribution.paymentMethod === 'CARD') {
                    <i class="pi pi-credit-card mr-2"></i>
                  } @else {
                    <i class="pi pi-building mr-2"></i>
                  }
                  {{ contribution.paymentMethod === 'CARD' ? 'Card' : 'Bank Transfer' }}
                  · {{ formatDate(contribution.paidAt) }}
                </p>
              </div>
            </div>
          </div>
        } @else if (contribution.bankDetails) {
          <!-- PENDING BANK TRANSFER: Show bank details -->
          <div class="overflow-hidden rounded-lg border border-greyscale-200 bg-white">
            <div class="border-b border-greyscale-200 bg-amber-50 p-4">
              <div class="flex items-center gap-2">
                <i class="pi pi-clock text-xl text-amber-600"></i>
                <h2 class="text-lg font-semibold text-amber-700">{{ 'contribute.bankTransfer.title' | transloco }}</h2>
              </div>
            </div>
            <div class="space-y-4 p-4">
              <!-- Warning message -->
              <div class="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p class="text-sm text-amber-800">
                  <i class="pi pi-info-circle mr-2"></i>
                  {{ 'contribute.bankTransfer.subtitle' | transloco }}
                </p>
              </div>

              <!-- IBAN -->
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500">IBAN</p>
                  <p class="font-mono font-medium text-gray-900">{{ contribution.bankDetails.iban }}</p>
                </div>
                <button
                  pButton
                  icon="pi pi-copy"
                  severity="secondary"
                  [text]="true"
                  [rounded]="true"
                  (click)="copyToClipboard(contribution.bankDetails.iban)"
                  aria-label="Copy IBAN"
                ></button>
              </div>

              <!-- BIC -->
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500">BIC/SWIFT</p>
                  <p class="font-mono font-medium text-gray-900">{{ contribution.bankDetails.bic }}</p>
                </div>
                <button
                  pButton
                  icon="pi pi-copy"
                  severity="secondary"
                  [text]="true"
                  [rounded]="true"
                  (click)="copyToClipboard(contribution.bankDetails.bic)"
                  aria-label="Copy BIC"
                ></button>
              </div>

              <!-- Beneficiary -->
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500">{{ 'contribute.bankTransfer.beneficiary' | transloco }}</p>
                  <p class="font-medium text-gray-900">{{ contribution.bankDetails.accountName }}</p>
                </div>
                <button
                  pButton
                  icon="pi pi-copy"
                  severity="secondary"
                  [text]="true"
                  [rounded]="true"
                  (click)="copyToClipboard(contribution.bankDetails.accountName)"
                  aria-label="Copy account name"
                ></button>
              </div>

              <!-- Reference - Highlighted -->
              <div class="rounded-lg border border-primary-200 bg-primary-50 p-3">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-medium text-primary-700">{{ 'contribute.bankTransfer.reference' | transloco }}</p>
                    <p class="font-mono text-lg font-bold text-primary-800">{{ contribution.bankDetails.reference }}</p>
                  </div>
                  <button
                    pButton
                    icon="pi pi-copy"
                    [rounded]="true"
                    (click)="copyToClipboard(contribution.bankDetails.reference)"
                    aria-label="Copy reference"
                  ></button>
                </div>
              </div>

              <!-- Amount -->
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500">{{ 'contribute.bankTransfer.amount' | transloco }}</p>
                  <p class="text-xl font-bold text-gray-900">{{ contribution.bankDetails.amount | number:'1.2-2' }} EUR</p>
                </div>
                <button
                  pButton
                  icon="pi pi-copy"
                  severity="secondary"
                  [text]="true"
                  [rounded]="true"
                  (click)="copyToClipboard(contribution.bankDetails.amount.toFixed(2))"
                  aria-label="Copy amount"
                ></button>
              </div>

              <!-- Important Notice -->
              <div class="rounded-lg border border-greyscale-200 bg-greyscale-50 p-3">
                <p class="text-sm font-semibold text-gray-700">
                  <i class="pi pi-info-circle mr-2"></i>
                  {{ 'contribute.bankTransfer.important' | transloco }}
                </p>
                <p class="mt-1 text-sm text-gray-600">
                  {{ 'contribute.bankTransfer.referenceWarning' | transloco }}
                </p>
              </div>

              <!-- Proforma Invoice Download -->
              @if (hasInvoice()) {
                <div class="border-t border-greyscale-200 pt-4">
                  <button
                    pButton
                    icon="pi pi-file-pdf"
                    severity="secondary"
                    (click)="downloadInvoice()"
                  >
                    {{ 'contribute.bankTransfer.downloadProforma' | transloco }}
                  </button>
                </div>
              }
            </div>
          </div>
        } @else {
          <!-- PENDING but no bank details (card processing or other) -->
          <div class="overflow-hidden rounded-lg border border-greyscale-200 bg-white">
            <div class="border-b border-greyscale-200 bg-amber-50 p-4">
              <div class="flex items-center gap-2">
                <i class="pi pi-clock text-xl text-amber-600"></i>
                <h2 class="text-lg font-semibold text-amber-700">{{ 'contribute.status.PENDING_PAYMENT' | transloco }}</h2>
              </div>
            </div>
            <div class="p-4">
              <p class="text-gray-600">
                {{ 'contribute.pending.message' | transloco }}
              </p>
            </div>
          </div>
        }
      </div>

      <!-- Right Column: Summary (sticky) -->
      <div class="lg:col-span-2 lg:sticky lg:top-4 lg:self-start">
        <div class="overflow-hidden rounded-lg border border-greyscale-200 bg-white">
          <div class="border-b border-greyscale-200 bg-greyscale-50 p-4">
            <h3 class="font-semibold text-gray-900">{{ 'contribute.summary.title' | transloco }}</h3>
          </div>
          <div class="space-y-3 p-4">
            <!-- Trip info -->
            <div>
              <p class="text-sm text-gray-500">{{ 'contribute.summary.trip' | transloco }}</p>
              <p class="font-medium text-gray-900">{{ contribution.trip.title }}</p>
            </div>
            <div class="flex gap-4">
              <div>
                <p class="text-sm text-gray-500">{{ 'contribute.summary.travelers' | transloco }}</p>
                <p class="font-medium text-gray-900">{{ contribution.trip.travellers }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-500">{{ 'contribute.summary.coverage' | transloco }}</p>
                <p class="font-medium text-gray-900">{{ contribution.globalPercentage }}%</p>
              </div>
            </div>

            <div class="border-t border-greyscale-200 pt-3">
              <!-- Project info -->
              <p class="text-sm text-gray-500">{{ 'contribute.summary.project' | transloco }}</p>
              <p class="font-medium text-gray-900">{{ getProjectName() }}</p>
              <p class="text-sm text-gray-600">
                {{ getTotalCO2() | number:'1.2-2' }} kg CO2 · {{ formatAmount(getProjectPricePerTonne()) }}/t
              </p>
            </div>

            <div class="border-t border-greyscale-200 pt-3">
              <!-- Pricing -->
              <div class="flex justify-between text-sm">
                <span class="text-gray-500">{{ 'contribute.pricing.subtotal' | transloco }}</span>
                <span class="font-medium">{{ formatAmount(getSubtotal()) }}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-500">{{ 'contribute.pricing.vat' | transloco }} (21%)</span>
                <span class="font-medium">{{ formatAmount(getVatAmount()) }}</span>
              </div>
              <div class="mt-2 flex justify-between border-t border-greyscale-200 pt-2">
                <span class="font-semibold text-gray-900">{{ 'contribute.pricing.total' | transloco }}</span>
                <span class="text-lg font-bold text-primary-600">{{ formatAmount(contribution.totalAmount) }}</span>
              </div>
            </div>

            <div class="border-t border-greyscale-200 pt-3">
              <!-- Certificate recipient -->
              <p class="text-sm text-gray-500">{{ 'contribute.summary.certificateFor' | transloco }}</p>
              <p class="font-medium text-gray-900">{{ contribution.certificateRecipientName }}</p>
              @if (!isPaid()) {
                <p class="mt-1 flex items-center gap-1 text-sm text-gray-500">
                  <i class="pi pi-file-pdf"></i>
                  {{ 'contribute.summary.certificateAfterPayment' | transloco }}
                </p>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  }
</main>

<p-toast />
